/**
 * Enginc Paywall System
 * Handles frontend paywall behavior and analytics
 */

/* global gtag, ga */

class EngincPaywallSystem {
	constructor() {
		this.settings = window.engincPaywall || {};
		this.paywallData = this.getPaywallData();
		this.modal = null;
		this.contentBlocked = false;

		if ( this.settings.showPaywall && this.paywallData.enabled ) {
			this.init();
		}
	}

	getPaywallData() {
		try {
			const dataElement = document.getElementById(
				'enginc-paywall-data'
			);
			return dataElement ? JSON.parse( dataElement.textContent ) : {};
		} catch ( e ) {
			console.warn( 'Enginc Paywall: Could not parse paywall data', e );
			return {};
		}
	}

	init() {
		// Wait for DOM to be ready
		if ( document.readyState === 'loading' ) {
			document.addEventListener( 'DOMContentLoaded', () =>
				this.initPaywall()
			);
		} else {
			this.initPaywall();
		}
	}

	initPaywall() {
		this.modal = document.getElementById( 'enginc-paywall-modal' );

		if ( this.modal ) {
			this.setupModalEventListeners();
			this.blockContent();
			this.lockScroll();
			this.showModal();

			this.trackEvent( 'paywall_shown', {
				articlesViewed: this.paywallData.articlesViewed,
				freeLimit: this.paywallData.freeLimit,
				url: window.location.href,
			} );
		}
	}

	setupModalEventListeners() {
		if ( ! this.modal ) return;

		// Close button placeholder. Currently no way to close the modal.
		const closeButtons = this.modal.querySelectorAll(
			'[data-paywall-close]'
		);
		closeButtons.forEach( ( btn ) => {
			btn.addEventListener( 'click', ( e ) => {
				e.preventDefault();
				this.closeModal();
			} );
		} );

		// Member action button
		const memberBtn = this.modal.querySelector(
			'[data-paywall-action="become-member"]'
		);
		if ( memberBtn ) {
			memberBtn.addEventListener( 'click', () => {
				this.trackEvent( 'become_member_clicked', {
					url: window.location.href,
					redirectUrl: this.settings.redirectUrl,
				} );
			} );
		}
	}

	showModal() {
		if ( this.modal ) {
			// Small delay to ensure proper rendering
			setTimeout( () => {
				this.modal.classList.remove( 'opacity-0', 'invisible' );
				this.modal.classList.add( 'show' );
				this.modal
					.querySelector( '.bg-white' )
					.classList.remove( '-translate-y-8' );
			}, 300 );
		}
	}

	closeModal() {
		if ( this.modal ) {
			this.modal.classList.add( 'opacity-0', 'invisible' );
			this.modal.classList.remove( 'show' );
			this.modal
				.querySelector( '.bg-white' )
				.classList.add( '-translate-y-8' );

			// Note: We DON'T unlock scroll on close since this is a paywall
			// Users should not be able to access content by closing the modal

			this.trackEvent( 'paywall_dismissed', {
				url: window.location.href,
			} );
		}
	}

	blockContent() {
		// Find the main content area using common selectors
		const contentSelectors = [
			'main .entry-content',
			'.post-content',
			'.content',
			'article .content',
			'.single-post .content',
			'[data-paywall-content]',
		];

		let contentArea = null;
		for ( const selector of contentSelectors ) {
			contentArea = document.querySelector( selector );
			if ( contentArea ) break;
		}

		if ( ! contentArea ) {
			console.warn(
				'Enginc Paywall: Could not find content area to block'
			);
			return;
		}

		// Add blocking classes
		contentArea.classList.add(
			'relative',
			'overflow-hidden',
			'select-none',
			'pointer-events-none'
		);

		contentArea.setAttribute( 'data-paywall-blocked', 'true' );

		this.contentBlocked = true;

		this.trackEvent( 'content_blocked', {
			url: window.location.href,
			contentSelector: contentArea.tagName.toLowerCase(),
		} );
	}

	unblockContent() {
		const contentArea = document.querySelector(
			'[data-paywall-blocked="true"]'
		);
		if ( contentArea ) {
			contentArea.classList.remove(
				'relative',
				'overflow-hidden',
				'select-none',
				'pointer-events-none'
			);
			contentArea.removeAttribute( 'data-paywall-blocked' );
		}

		this.contentBlocked = false;
	}

	lockScroll() {
		// Store original scroll position
		this.scrollPosition =
			window.pageYOffset || document.documentElement.scrollTop;

		// Add scroll lock classes to body
		document.body.style.position = 'fixed';
		document.body.style.top = `-${ this.scrollPosition }px`;
		document.body.style.width = '100%';
		document.body.classList.add( 'paywall-scroll-locked' );

		// Bind methods to preserve 'this' context
		this.boundPreventScroll = this.preventScroll.bind( this );
		this.boundPreventKeyboardScroll =
			this.preventKeyboardScroll.bind( this );

		// Prevent scrolling on modal
		if ( this.modal ) {
			this.modal.addEventListener( 'wheel', this.boundPreventScroll, {
				passive: false,
			} );
			this.modal.addEventListener( 'touchmove', this.boundPreventScroll, {
				passive: false,
			} );
		}

		// Prevent keyboard scrolling
		document.addEventListener( 'keydown', this.boundPreventKeyboardScroll );

		// Also prevent scrolling on document body
		document.addEventListener( 'wheel', this.boundPreventScroll, {
			passive: false,
		} );
		document.addEventListener( 'touchmove', this.boundPreventScroll, {
			passive: false,
		} );
	}

	unlockScroll() {
		// Remove scroll lock styles
		document.body.style.position = '';
		document.body.style.top = '';
		document.body.style.width = '';
		document.body.classList.remove( 'paywall-scroll-locked' );

		// Restore scroll position
		if ( this.scrollPosition ) {
			window.scrollTo( 0, this.scrollPosition );
		}

		// Remove event listeners
		if ( this.modal && this.boundPreventScroll ) {
			this.modal.removeEventListener( 'wheel', this.boundPreventScroll );
			this.modal.removeEventListener(
				'touchmove',
				this.boundPreventScroll
			);
		}

		if ( this.boundPreventKeyboardScroll ) {
			document.removeEventListener(
				'keydown',
				this.boundPreventKeyboardScroll
			);
		}

		if ( this.boundPreventScroll ) {
			document.removeEventListener( 'wheel', this.boundPreventScroll );
			document.removeEventListener(
				'touchmove',
				this.boundPreventScroll
			);
		}
	}

	preventScroll( e ) {
		e.preventDefault();
		e.stopPropagation();
		return false;
	}

	preventKeyboardScroll( e ) {
		// Block scroll-related keys
		const scrollKeys = [
			32, // Space
			33, // Page Up
			34, // Page Down
			35, // End
			36, // Home
			37, // Left Arrow
			38, // Up Arrow
			39, // Right Arrow
			40, // Down Arrow
		];

		if ( scrollKeys.includes( e.keyCode ) ) {
			e.preventDefault();
			e.stopPropagation();
			return false;
		}
	}

	trackEvent( eventType, data = {} ) {
		// Fire custom events for analytics integration
		const customEvent = new CustomEvent( 'enginc-paywall-event', {
			detail: {
				type: eventType,
				data: {
					...data,
					timestamp: new Date().toISOString(),
					userAgent: navigator.userAgent,
					referrer: document.referrer,
				},
			},
		} );

		document.dispatchEvent( customEvent );

		// Log to console in development
		if (
			window.location.hostname === 'localhost' ||
			window.location.hostname.includes( '.test' )
		) {
			console.log( 'Enginc Paywall Event:', eventType, data );
		}

		// Integrate with analytics platforms
		this.sendToAnalytics( eventType, data );
	}

	sendToAnalytics( eventType, data ) {
		// Google Analytics 4
		if ( typeof gtag !== 'undefined' ) {
			gtag( 'event', eventType, {
				event_category: 'paywall',
				custom_parameters: data,
			} );
		}

		// Google Analytics Universal
		if ( typeof ga !== 'undefined' ) {
			ga( 'send', 'event', 'Paywall', eventType, JSON.stringify( data ) );
		}
	}
}

// Initialize paywall system
document.addEventListener( 'DOMContentLoaded', () => {
	window.engincPaywallInstance = new EngincPaywallSystem();
} );

// Export for external access
window.EngincPaywallSystem = EngincPaywallSystem;
